<template>
  <div>
    <div class="flex flex-col gap-4">
      <h3>{{ isNewCarSwap ? t('WhenAndWhere') : t('editTimeAndLocation') }}</h3>
      <TimeAndLocationFields
        :key="mode"
        :booking="booking"
        :prefix="prefix"
        :mode="mode"
        :allocation-index="allocationIndex"
        @change-ready-to-save="readyToSave = $event"
        @change-price-preview="pricePreview = $event"
      />
    </div>

    <Divider />

    <div class="flex justify-between gap-2">
      <div
        v-if="
          pricePreview.isLoading ||
          pricePreview.transferPrice !== null ||
          pricePreview.totalPrice !== null
        "
        class="text-sm"
      >
        <div v-if="pricePreview.isLoading">{{ t('calculatingPrices') }}</div>
        <div v-else class="grid w-fit grid-cols-[auto_auto] gap-x-2">
          <template v-if="pricePreview.transferPrice !== null">
            <span class="font-medium">{{ t('transferPrice') }}: </span>
            <span v-currency="pricePreview.transferPrice" class="text-right" />
          </template>
          <template v-if="pricePreview.totalPrice !== null">
            <span class="font-medium"
              >{{ t(isLongTerm ? 'contractTotal' : 'newTotalPrice') }}:
            </span>
            <span v-currency="pricePreview.totalPrice" class="text-right" />
          </template>
        </div>
      </div>

      <div class="flex flex-1 justify-end gap-2">
        <CVButton
          variant="default"
          :disabled="!readyToSave"
          :is-loading="isSaving"
          @click="emit('confirm')"
          >{{ confirmLabel }}</CVButton
        >
        <CVButton :disabled="isSaving" @click="handleCancel">{{ t('cancel') }}</CVButton>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { computed, ref } from 'vue';
import CVButton from '../buttons/CVButton.vue';
import type { Booking } from '@/entities/bookings/booking.entity';
import Divider from '../Divider.vue';
import TimeAndLocationFields from './TimeAndLocationFields.vue';
import type { PricePreview } from '@/hooks/use-transfer-price-preview';
import { isLongTermPricing } from '@/utils/pricing.utils';

const props = defineProps<{
  booking: Booking;
  allocationIndex: number;
  mode: 'pickup' | 'dropoff';
  isNewCarSwap?: boolean;
  isSaving?: boolean;
}>();

const emit = defineEmits<{
  (e: 'cancel'): void;
  (e: 'confirm'): void;
  (e: 'back'): void;
}>();

const { t } = useI18n();

const isLongTerm = computed(() => isLongTermPricing(props.booking.priceCalculation));

const readyToSave = ref(false);
const pricePreview = ref<PricePreview>({
  transferPrice: null,
  totalPrice: null,
  isLoading: false,
});

const prefix = computed(() =>
  (props.allocationIndex === 0 && props.mode === 'pickup') ||
  (props.allocationIndex === props.booking.allocations.length - 1 &&
    props.mode === 'dropoff' &&
    !props.isNewCarSwap)
    ? ''
    : `allocations.[${props.allocationIndex}]`,
);

const confirmLabel = computed(() => {
  if (props.isNewCarSwap) {
    return t('selectCar');
  } else {
    return t('save');
  }
});
const handleCancel = () => {
  emit('cancel');
};
</script>

<i18n lang="json">
{
  "en": {
    "WhenAndWhere": "When and where should the replacement car be delivered?",
    "editTimeAndLocation": "Edit time and location",
    "selectCar": "Select car",
    "transferPrice": "Transfer price",
    "newTotalPrice": "New total price",
    "contractTotal": "Contract total",
    "calculatingPrices": "Calculating prices..."
  },
  "de": {
    "WhenAndWhere": "Wann und wo soll der Ersatzwagen angeliefert werden?",
    "editTimeAndLocation": "Zeit und Ort bearbeiten",
    "selectCar": "Auto auswählen",
    "transferPrice": "Transferpreis",
    "newTotalPrice": "Neuer Gesamtpreis",
    "contractTotal": "Vertragssumme",
    "calculatingPrices": "Preise werden berechnet..."
  },
  "es": {
    "WhenAndWhere": "¿Cuándo y dónde debe entregarse el vehículo de sustitución?",
    "editTimeAndLocation": "Editar hora y lugar",
    "selectCar": "Seleccionar vehículo",
    "transferPrice": "Precio del traslado",
    "newTotalPrice": "Nuevo precio total",
    "contractTotal": "Total del contrato",
    "calculatingPrices": "Calculando precios..."
  }
}
</i18n>
